// Single entry point for game controls: one router fed by every source, captured by the running session.
import { InputRouter, routeKey, keyBindings, keyLabel, type ActionDetail, type GameAction, type InputSource } from './input.js';
import { GamepadPoller } from './gamepad.js';
import { state } from '../data/store.js';

export const input = new InputRouter();
export const gamepads = new GamepadPoller();

/** Keyboard bindings in force (custom ones from the settings, otherwise the defaults). */
export const currentKeys = () => keyBindings(state.settings.keyMap);

/** Keys bound to an action, for the settings and the pause menus; Escape always pauses. */
export function keysFor(action: GameAction, names: { space: string; esc: string }): string[] {
  const map = currentKeys(), keys = Object.keys(map).filter(k => map[k] === action).map(k => keyLabel(k, names));
  return action === 'pause' ? [names.esc, ...keys] : keys;
}

/** What the running session needs from a controller; 'idle' when no session captures the controls. */
export type ControlLayout = 'idle' | 'race' | 'workout' | 'game';
let layout: ControlLayout = 'idle';
const layoutListeners = new Set<(l: ControlLayout) => void>();
export const controlLayout = (): ControlLayout => layout;
/** Follows the layout (a paired phone shows the matching buttons). Returns the unsubscribe. */
export function onControlLayout(fn: (l: ControlLayout) => void): () => void {
  layoutListeners.add(fn);
  return () => { layoutListeners.delete(fn); };
}
function setLayout(l: ControlLayout) {
  if (l === layout) return;
  layout = l;
  for (const fn of layoutListeners) fn(l);
}

export interface Capture {
  /** Buttons the session uses (race by default). */
  layout?: Exclude<ControlLayout, 'idle'>;
  /** Game actions, whatever their source (transitions only: one down, one up). */
  onAction: (action: GameAction, down: boolean, source: InputSource) => void;
  /** Keys that are not game actions (simulated watts, workout intensity…), on key down. */
  onOtherKey?: (e: KeyboardEvent) => void;
}

/** Starts listening to every source for a session. The returned function stops and releases everything. */
export function captureInput(c: Capture): () => void {
  const kd = (e: KeyboardEvent) => { if (!routeKey(input, e, true, currentKeys())) c.onOtherKey?.(e); };
  const ku = (e: KeyboardEvent) => { routeKey(input, e, false, currentKeys()); };
  const act = (e: Event) => { const d = (e as CustomEvent<ActionDetail>).detail; c.onAction(d.action, d.down, d.source); };
  // a key released while the window had no focus never sends keyup: release what the keyboard held
  const blur = () => input.releaseSource('keyboard');
  const hidden = () => { if (document.visibilityState === 'hidden') { input.releaseSource('keyboard'); input.releaseSource('touch'); } };
  input.releaseAll();
  addEventListener('keydown', kd); addEventListener('keyup', ku); addEventListener('blur', blur);
  document.addEventListener('visibilitychange', hidden);
  input.addEventListener('action', act);
  gamepads.start((control, action, down) => input.press('gamepad', control, action, down));
  setLayout(c.layout ?? 'race');
  return () => {
    removeEventListener('keydown', kd); removeEventListener('keyup', ku); removeEventListener('blur', blur);
    document.removeEventListener('visibilitychange', hidden);
    input.removeEventListener('action', act);
    gamepads.stop();
    input.releaseAll();
    setLayout('idle');
  };
}
